<template>
  <div class="container-fluid bg-white vh-100 p-0">
    <ClientLogo v-if="this.$route.name != 'behalf_application'" />
    <BreadCrumb label="Employee Screening Service" />

    <!-- Main Content -->
    <div class="d-flex justify-content-center align-items-start min-vh-50 row">
      <div class="col-11 col-sm-8 p-3">
        <!-- Mobile step title - only visible on mobile -->
        <h4 class="mobile-step-title" v-if="all_steps.length > 1">
          {{ current_step_data.title || "Loading..." }}
          <small class="text-muted"
            >(Step {{ current_step + 1 }}/{{ all_steps.length }})</small
          >
        </h4>

        <!-- Step Wizard Progress -->
        <div class="step-wizard-container" v-if="all_steps.length > 1">
          <div class="step-indicators">
            <div
              v-for="(step, index) in all_steps"
              :key="index"
              class="step-indicator"
              :class="{
                active: index === current_step,
                completed: step.is_valid || index < current_step,
              }"
              @click="index < current_step ? (current_step = index) : null"
            >
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-title">{{ step.title }}</div>
            </div>
          </div>
        </div>

        <!-- Step Content Card -->
        <div class="main-content-card">
          <div class="content-body">
            <!-- Candidate Component (Always first step) -->
            <div
              v-if="current_step === 0 && current_step_data.service_id"
              class="form-section"
            >
              <component
                :is="candidate_component"
                :ref="current_step_data.key"
                :key="current_step_data.key + '_' + dynamic_key"
                :service_id="current_step_data.service_id"
                :criminal_check_included="
                  current_step_data.criminal_check_included
                "
                :academic_checks_included="
                  current_step_data.academic_checks_included
                "
                :section_id="current_step_data.section_id"
                @update_data="handle_data_update"
              />
            </div>

            <!-- Assigned Service Components -->
            <div v-else-if="current_step_data.component" class="form-section">
              <!-- Special handling for consent form -->
              <component
                v-if="current_step_data.key === 'consent_document_signing'"
                :is="current_step_data.component"
                :ref="current_step_data.key"
                :key="'consent_' + current_step_data.key + '_' + dynamic_key"
                @consent_completed="handle_consent_completed"
              />
              <!-- Regular service components -->
              <component
                v-else
                :is="current_step_data.component"
                :ref="current_step_data.key"
                :key="'regular_' + current_step_data.key + '_' + dynamic_key"
                :service_id="current_step_data.service_id"
                :criminal_check_included="
                  current_step_data.criminal_check_included
                "
                :academic_checks_included="
                  current_step_data.academic_checks_included
                "
                :section_id="current_step_data.section_id"
                @update_data="handle_data_update"
              />
            </div>

            <!-- Loading State -->
            <div v-else-if="loading" class="loading-section">
              <div class="loading-spinner"></div>
              <h3>Loading Services</h3>
              <p>Please wait while we prepare your screening services...</p>
            </div>

            <!-- No Services Message -->
            <div v-else class="empty-state">
              <i class="fas fa-exclamation-triangle"></i>
              <h3>No Services Available</h3>
              <p>
                No screening services have been configured for this workflow.
              </p>
            </div>
          </div>

          <!-- Navigation Footer -->
          <div class="content-footer" v-if="!loading && all_steps.length > 0">
            <div class="navigation-buttons">
              <button
                type="button"
                class="btn-bg-secondary btn btn-sm text-xs shadow rounded-3 p-2"
                :disabled="current_step === 0"
                @click="previous_step"
                v-if="all_steps.length > 1"
              >
                <i class="fas fa-chevron-left"></i>
                Previous
              </button>

              <div v-else></div>

              <button
                v-if="current_step_data.key !== 'consent_document_signing'"
                type="button"
                class="btn-bg-green btn btn-sm text-xs shadow rounded-3 p-2"
                @click="next_step"
              >
                {{ is_last_step ? "Complete Screening" : "Continue" }}
                <i
                  class="fas"
                  :class="is_last_step ? 'fa-check' : 'fa-chevron-right'"
                ></i>
              </button>
            </div>

            <!-- Service Steps Indicator for Mobile -->
            <div class="mobile-steps" v-if="all_steps.length > 1">
              <div class="steps-container">
                <div
                  v-for="(step, index) in all_steps"
                  :key="index"
                  class="step-indicator-dot"
                  :class="{
                    active: index === current_step,
                    completed: index < current_step,
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import Swal from "sweetalert2";

// Import all employee screening components
import CandidateComponent from "./candidate/CandidateComponent.vue";
import AcademicChecks from "./academic_checks/AcademicChecks.vue";
import DriversLicense from "./drivers_license/DriversLicense.vue";
import EmploymentHistory from "./employment_history/EmploymentHistory.vue";
import IdecoCriminal from "./ideco_criminal/IdecoCriminal.vue";
import ProfessionalAssociation from "./professional_association/ProfessionalAssociation.vue";
import ProfessionalDriversLicense from "./professional_drivers_license/ProfessionalDriversLicense.vue";
import DocusealSigning from "./consent_form/DocusealSigning.vue";

export default {
  name: "EmployeeScreeningService",
  mixins: [MAIN_CONTROLLER],
  components: {
    BreadCrumb,
    ClientLogo,
    CandidateComponent,
    AcademicChecks,
    DriversLicense,
    EmploymentHistory,
    IdecoCriminal,
    ProfessionalAssociation,
    ProfessionalDriversLicense,
    DocusealSigning,
  },
  data() {
    return {
      current_step: 0, // Start at 0, set for testing purposes
      dynamic_key: 0, // Used to force re-render of components when data changes
      loading: true,
      is_completed: false,
      candidate_component: CandidateComponent,

      // Service component mapping - Updated to match API service names
      service_components: {
        "Academic Qualification Check": AcademicChecks,
        "Driver License Check": DriversLicense,
        "Employment History Check": EmploymentHistory,
        "Ideco Criminal Check": IdecoCriminal,
        "Professional Association Check": ProfessionalAssociation,
        "Professional Driver License Check": ProfessionalDriversLicense,
        "Consent Document Signing": DocusealSigning,
      },

      // Available services from API
      available_services: [],
      assigned_services: [],
      // Validation states
      step_validation: {},
    };
  },
  computed: {
    workflow_id() {
      return localStorage.getItem("workflow_id") || null;
    },
    all_steps() {
      var candidate_service = this.assigned_services.find(
        (service) => service.service_name === "Candidate Screening"
      );

      if (!candidate_service) {
        return [];
      }

      var criminal_check_included = this.assigned_services.some(
        (service) => service.service_name === "Ideco Criminal Check"
      );
      var academic_checks_included = this.assigned_services.some(
        (service) => service.service_name === "Academic Qualification Check"
      );

      const steps = [
        {
          section_id: candidate_service.id,
          key: "candidate",
          title: "Candidate Information",
          description: "Personal details required for all background checks",
          component: null, // Special case for candidate
          service_id: candidate_service.service_id,
          service_order: 0,
          required: candidate_service.required,
          is_valid: this.step_validation.candidate || false,
          criminal_check_included: criminal_check_included,
          academic_checks_included: academic_checks_included,
          key_groups: candidate_service.key_groups || [],
        },
      ];

      // Add assigned services as steps (excluding Candidate Screening and Consent Document Signing)
      this.assigned_services
        .filter(
          (service) =>
            service.service_name !== "Candidate Screening" &&
            service.service_name !== "Consent Document Signing"
        )
        .sort((a, b) => a.service_order - b.service_order)
        .forEach((service) => {
          const component = this.service_components[service.service_name];
          if (component) {
            const step_key = service.service_name
              .toLowerCase()
              .replace(/[^a-z0-9]/g, "_");
            steps.push({
              section_id: service.id,
              key: step_key,
              title: service.service_name,
              description: this.get_service_description(service.service_name),
              component: component,
              service_id: service.service_id,
              service_order: service.service_order,
              required: true,
              is_valid: this.step_validation[step_key] || false,
              criminal_check_included: false, // Default to false unless specified
              academic_checks_included: false, // Default to false unless specified
              key_groups: service.key_groups || [],
            });
          }
        });

      // Always add DocusealSigning step at the very end if consent service exists
      const consent_service = this.assigned_services.find(
        (service) =>
          service.service_name === "Consent Document Signing" ||
          service.service_name === "Docuseal Documents"
      );
      if (consent_service) {
        steps.push({
          section_id: consent_service.id,
          key: "consent_document_signing",
          title: "Consent Document Signing",
          description:
            "Sign the consent document to complete your screening process.",
          component: DocusealSigning,
          service_id: consent_service.service_id,
          service_order: 999, // Ensure it's always last
          required: true,
          is_valid: this.step_validation["consent_document_signing"] || false,
        });
      }
      return steps;
    },
    current_step_data() {
      return this.all_steps[this.current_step] || {};
    },

    is_last_step() {
      return this.current_step === this.all_steps.length - 1;
    },

    progress_percentage() {
      if (this.all_steps.length === 0) return 0;
      return ((this.current_step + 1) / this.all_steps.length) * 100;
    },

    completed_services() {
      return this.all_steps.slice(1); // Exclude candidate step
    },
  },
  mounted() {
    this.initialize_service();
  },
  methods: {
    get_customer_data() {
      const TOKEN = localStorage.getItem("token");
      const CUSTOMER_DATA_URL = `${API_BASE_URL}/verify_api/system_management_api/get_customer_data/`;

      fetch(CUSTOMER_DATA_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${TOKEN}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          var customer_data = data || {};
          if (this.current_step === 0) {
            this.$refs.candidate.set_customer_data(customer_data);
          }
        })
        .catch((error) => {
          console.error("Error fetching customer data:", error);
        });
    },
    rerender_current_component() {
      // Force re-render of the current step component
      this.dynamic_key++;
    },
    async initialize_service() {
      try {
        await this.get_assigned_services();

        this.loading = false;
      } catch (error) {
        console.error("Error initializing employee screening service:", error);
        this.loading = false;
      } finally {
        this.get_customer_data();
      }
    },

    async get_assigned_services() {
      const token = localStorage.getItem("token");
      const workflow_id = this.workflow_id;

      if (!workflow_id) {
        console.warn("No workflow project service ID provided");
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/verify_api/employee_screening/api/get_workflow_sections/?workflow_id=${workflow_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          }
        );

        const data = await response.json();
        if (data.status === "success" && data.data) {
          this.assigned_services = data.data.sections || [];
        }
      } catch (error) {
        console.error("Error fetching assigned services:", error);
        this.assigned_services = [];
      }
    },

    get_service_description(service_name) {
      const descriptions = {
        "Academic Qualification Check":
          "Verify academic credentials including certificate details and institution information",
        "Driver License Check":
          "Verify driver's license information and driving record",
        "Employment History Check":
          "Detailed employment verification including employer contact information",
        "Ideco Criminal Check":
          "Comprehensive criminal background verification",
        "Professional Association Check":
          "Verify professional association memberships and certifications",
        "Professional Driver License Check":
          "Verify professional driving permits and commercial driver qualifications",
        "Candidate Screening":
          "Personal details required for all background checks and identity verification",
      };
      return descriptions[service_name] || "Additional screening verification";
    },

    get_step_icon(index) {
      if (index < this.current_step) return "fa-check-circle text-success";
      if (index === this.current_step) return "fa-circle text-primary";
      return "fa-circle text-muted";
    },

    async next_step() {
      // Handle consent form completion differently
      if (this.current_step_data.key === "consent_document_signing") {
        // For consent form, don't do validation here - it's handled by the DocusealSigning component
        // The component will emit completion event when done
        return;
      }

      // Validate current step before proceeding
      if (this.current_step_data.key === "candidate") {
        // Special case for candidate step validation
        const ok = await this.$refs.candidate.handle_data_submit();
        if (!ok) {
          console.warn("Candidate data is invalid");
          return;
        }
      } else {
        const current_ref = this.$refs[this.current_step_data.key];
        if (
          current_ref &&
          typeof current_ref.handle_data_submit === "function"
        ) {
          const ok = await current_ref.handle_data_submit();
          if (!ok) {
            console.warn(`${this.current_step_data.key} data is invalid`);
            return;
          }
        }
      }
    },

    previous_step() {
      if (this.current_step > 0) {
        this.current_step--;
      }
    },

    complete_screening() {
      this.is_completed = true;
      const token = localStorage.getItem("token");
      const workflow_id = this.workflow_id;
      fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/service/complete/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ workflow_id }),
        }
      )
        .then(() => {
          this.fetch_current_service();
        })
        .catch((error) => {
          console.error("Error completing service:", error);
        });
    },

    async multi_data_processing(data_type, data_list) {
      var data_processed = false;

      if (!Array.isArray(data_list) || data_list.length === 0) {
        console.warn("No data to process");
        return false;
      }

      for (const data of data_list) {
        if (!data || typeof data !== "object") {
          console.warn("Invalid data item:", data);
          data_processed = false;
          break; // Exit loop immediately if any item is invalid
        }
        // Process each data item individually
        var result = await this.single_data_processing(data_type, data);
        if (result) {
          data_processed = true;
        } else {
          console.warn("Failed to process data item:", data);
          data_processed = false;
          break; // Exit loop immediately if processing fails
        }
      }

      return data_processed;
    },

    async single_data_processing(data_type, data) {
      const section_id = this.current_step_data.section_id;
      const token = localStorage.getItem("token");
      let payload = {
        employee_screening_service_section_id: section_id,
        keys: data,
      };
      let url = `${API_BASE_URL}/verify_api/employee_screening/api/keys/create/`;
      let method = "POST";

      if (data && data.id) {
        var employee_screening_service_key_group_id = data.id;
        delete data.id; // Remove ID for update operation
        payload = {
          employee_screening_service_key_group_id:
            employee_screening_service_key_group_id,
          keys: data,
        };
        url = `${API_BASE_URL}/verify_api/employee_screening/api/keys/update/`;
      } else {
        delete data.id; // Remove ID for create operation
      }

      if (
        data_type === "academic" ||
        data_type === "employment" ||
        data_type === "professional_association"
      ) {
        switch (data_type) {
          case "academic": {
            // Remove unwanted properties from academic data
            let academic_category_name = "";
            if (data.institution_region || data.is_matric) {
              if (data.is_matric == "Yes") {
                academic_category_name = "Matric";
              } else if (data.institution_region === "South Africa") {
                academic_category_name = "South African Only";
              } else if (data.institution_region === "Africa") {
                academic_category_name = "Africa";
              } else if (data.institution_region === "Global") {
                academic_category_name = "Global";
              }
              if (academic_category_name) {
                delete data.institution_region;
                delete data.is_matric;
                const academic_rest = { ...data };
                payload.keys = academic_rest;
                payload.category_name = academic_category_name;
              } else {
                console.warn(
                  "Invalid institution region:",
                  data.institution_region
                );
              }
            }
            break;
          }
          case "employment": {
            // Remove unwanted properties from employment data
            let employment_category_name = "";
            if (data.employment_region) {
              if (data.employment_region === "South Africa") {
                employment_category_name = "South African Only";
              } else if (data.employment_region === "Africa") {
                employment_category_name = "Africa";
              } else if (data.employment_region === "Global") {
                employment_category_name = "Global";
              }
              if (employment_category_name) {
                delete data.employment_region;
                const employment_rest = { ...data };
                payload.keys = employment_rest;
                payload.category_name = employment_category_name;
              } else {
                console.warn(
                  "Invalid employment region:",
                  data.employment_region
                );
              }
            }
            break;
          }
          case "professional_association": {
            // Remove unwanted properties from professional association data
            let association_category_name = "";
            if (data.association_region) {
              if (data.association_region === "South Africa") {
                association_category_name = "South African Only";
              } else if (data.association_region === "Africa") {
                association_category_name = "Africa";
              } else if (data.association_region === "Global") {
                association_category_name = "Global";
              }
              if (association_category_name) {
                delete data.association_region;
                const association_rest = { ...data };
                payload.keys = association_rest;
                payload.category_name = association_category_name;
              } else {
                console.warn(
                  "Invalid association region:",
                  data.association_region
                );
              }
            }
            break;
          }
          default:
            break;
        }
      }

      try {
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (result.status === "success") {
          return true;
        } else {
          let error_msg = "";
          if (typeof result.message === "object") {
            error_msg =
              "Error details:\n```json\n" +
              JSON.stringify(result.message, null, 2) +
              "\n```";
          } else {
            error_msg =
              result.message || "An error occurred while saving data.";
          }
          Swal.fire({
            icon: "error",
            title: "Error",
            html: error_msg,
            timer: 3000,
            showConfirmButton: false,
          });
          return false;
        }
      } catch (err) {
        console.error("Error submitting key data:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An unexpected error occurred.",
          timer: 3000,
          showConfirmButton: false,
        });
        return false;
      }
    },
    async handle_data_update(data_type, data) {
      // Update form data based on the type of data received
      const isEmptyObject = (obj) =>
        obj && !Array.isArray(obj) && Object.keys(obj).length === 0;
      const isEmptyArray = Array.isArray(data) && data.length === 0;

      if (isEmptyObject(data) || isEmptyArray) {
        console.warn(
          `No data submitted for ${this.current_step_data.key} step`
        );
        this.mark_step_invalid(this.current_step_data.key);
        return false;
      }
      var data_processed = false;

      switch (data_type) {
        case "candidate":
          data_processed = await this.single_data_processing(data_type, data);
          break;
        case "academic":
          data_processed = await this.multi_data_processing(data_type, data);
          break;
        case "drivers_license":
          data_processed = await this.single_data_processing(data_type, data);
          break;
        case "employment":
          data_processed = await this.multi_data_processing(data_type, data);
          break;
        case "ideco":
          data_processed = await this.single_data_processing(data_type, data);
          break;
        case "professional_association":
          data_processed = await this.multi_data_processing(data_type, data);
          break;
        case "professional_drivers_license":
          data_processed = await this.single_data_processing(data_type, data);
          break;
        default:
          data_processed = false;
          break;
      }
      if (data_processed) {
        const saved_title = this.current_step_data.title || "Data";
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: `${saved_title} saved successfully.`,
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          this.mark_step_valid(this.current_step_data.key);

          // Move to next step or complete screening
          if (this.is_last_step) {
            this.complete_screening();
          } else {
            this.current_step++;
          }
        });
      }
    },

    // Step validation methods
    mark_step_valid(step_key) {
      this.step_validation[step_key] = true;
    },

    mark_step_invalid(step_key) {
      this.step_validation[step_key] = false;
    },

    handle_consent_completed(result) {
      if (result.success) {
        // Mark consent step as valid
        this.mark_step_valid("consent_document_signing");

        // Show success message
        Swal.fire({
          icon: "success",
          title: "Document Signed!",
          text: "Consent document has been signed successfully.",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          // Complete the screening process
          this.complete_screening();
        });
      } else {
        // Handle error case
        this.mark_step_invalid("consent_document_signing");

        Swal.fire({
          icon: "error",
          title: "Signing Failed",
          text:
            result.error ||
            "Failed to complete document signing. Please try again.",
          confirmButtonText: "OK",
        });
      }
    },
  },
};
</script>

<style scoped>
/* Reset and Base Styles */
* {
  box-sizing: border-box;
}

.container-fluid {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Step Wizard Styling */
.step-wizard-container {
  margin-bottom: 24px;
}

.step-indicators {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 0 -8px;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0 8px;
  flex: 1;
  min-width: 70px;
  margin-bottom: 5px;
  cursor: pointer;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 0.5rem;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.step-title {
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease;
}

.step-indicator.active .step-number {
  background-color: white;
  color: var(--primary-color, #007bff);
  border-color: var(--primary-color, #007bff);
}

.step-indicator.active .step-title {
  color: var(--primary-color, #007bff);
  font-weight: 600;
}

.step-indicator.completed .step-number {
  background-color: var(--primary-color, #007bff);
  color: white;
  border-color: var(--primary-color, #007bff);
}

/* Mobile-friendly enhancements */
.mobile-step-title {
  display: none;
}

/* For mobile and tablet responsiveness */
@media (max-width: 991px) {
  .step-indicators {
    overflow-x: auto;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding-bottom: 10px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE/Edge */
  }

  .step-indicators::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, Opera */
  }

  .step-indicator {
    flex: 0 0 auto;
    min-width: 70px;
  }

  .mobile-step-title {
    display: block;
    margin: 0.5rem 0 1.5rem;
    text-align: center;
    font-size: 1.2rem;
    font-weight: 600;
  }
}

/* Specific adjustments for very small screens */
@media (max-width: 575px) {
  .step-number {
    width: 24px;
    height: 24px;
    font-size: 0.8rem;
  }

  .step-title {
    font-size: 0.7rem;
  }
}

/* Main Content Card */
.main-content-card {
  background: white;
  overflow: hidden;
}

/* Content Body */
.content-body {
  padding: 20px 40px;
}

.form-section {
  max-width: 100%;
}

/* Form Styling - Global overrides for child components */
.form-section :deep(.service-header) {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
}

.form-section :deep(.service-header h6) {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.form-section :deep(.service-header p) {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.form-section :deep(.row) {
  margin: 0 -12px;
}

.form-section :deep(.col-md-6),
.form-section :deep(.col-md-12),
.form-section :deep(.col-sm-12) {
  padding: 0 6px;
  margin-bottom: 6px;
}

.form-section :deep(.form-label) {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
  display: block;
}

.form-section :deep(.form-control),
.form-section :deep(.form-select) {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
  background-color: white;
}

.form-section :deep(.form-control:focus),
.form-section :deep(.form-select:focus) {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
  outline: none;
}

.form-section :deep(.form-control::placeholder) {
  color: #adb5bd;
}

.form-section :deep(.form-check) {
  padding-left: 0;
  margin-bottom: 16px;
}

.form-section :deep(.form-check-input) {
  margin-right: 12px;
  width: 20px;
  height: 20px;
}

.form-section :deep(.form-check-label) {
  color: #495057;
  font-size: 14px;
  cursor: pointer;
}

/* Loading State */
.loading-section {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e9ecef;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

.loading-section h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.loading-section p {
  color: #6c757d;
  font-size: 14px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state i {
  font-size: 48px;
  color: #ffc107;
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.empty-state p {
  color: #6c757d;
  font-size: 14px;
}

/* Content Footer */
.content-footer {
  padding: 6px 40px;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

/* Button Styles */
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Mobile Steps */
.mobile-steps {
  display: none;
}

.steps-container {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.step-indicator-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #dee2e6;
  transition: all 0.3s ease;
}

.step-indicator-dot.active {
  background-color: #007bff;
  transform: scale(1.2);
}

.step-indicator-dot.completed {
  background-color: #28a745;
}

/* Animations */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.completion-section {
  animation: fadeInUp 0.5s ease-out;
}

/* Responsive Design */
@media (max-width: 768px) {
  .content-body {
    padding: 24px 20px;
  }

  .content-footer {
    padding: 20px;
  }

  .navigation-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .btn-bg-green,
  .btn-bg-secondary,
  .btn-bg-success {
    width: 100%;
    justify-content: center;
  }

  .mobile-steps {
    display: block;
    margin-top: 16px;
  }

  .step-wizard-container {
    padding: 16px;
    background: white;
    border-radius: 12px;
    margin-bottom: 16px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  }

  .success-card {
    padding: 24px 20px;
  }

  .success-header {
    flex-direction: column;
    text-align: center;
  }

  .success-actions {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .container-fluid .col-11,
  .container-fluid .col-sm-10 {
    padding: 12px;
  }

  .form-section :deep(.col-md-6) {
    padding: 0 6px;
  }

  .header-text h2 {
    font-size: 18px;
  }

  .header-text p {
    font-size: 14px;
  }

  .content-body {
    padding: 20px 16px;
  }

  .main-content-card {
    border-radius: 12px;
  }
}

/* Consent step info styling */
.consent-step-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.consent-step-info p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
}

.consent-step-info i {
  margin-right: 8px;
  color: #007bff;
}

.text-muted {
  color: #6c757d !important;
}

.small {
  font-size: 0.875em;
}

.mb-0 {
  margin-bottom: 0 !important;
}
</style>
